/**
 * EmailAuditEmbed · workflow-level "View Email" affordance (ARC-014).
 *
 * Drop this component anywhere a workflow sends email. It fetches the
 * latest audit rows scoped by `workflowPrefix` and lets the operator
 * inspect exactly what would have been sent (Preview) or what was
 * transmitted (Production) without navigating away.
 *
 * Props:
 *   workflowPrefix   — string prefix matched against `workflow` field
 *                      (server-side regex-friendly; we filter client-side
 *                      by startsWith for now — small dataset).
 *   title            — optional header override
 *   limit            — how many rows to fetch (default 5)
 */
import { useEffect, useState } from "react";
import { EnvelopeSimple, Prohibit, CheckCircle, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function EmailAuditEmbed({ workflowPrefix, title = "Email activity", limit = 5 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Fetch broadly then filter by prefix so any suffix (dates,
        // supplier names, notification IDs) still matches.
        const { data } = await api.get("/email-audit", { params: { limit: 25 } });
        if (!alive) return;
        const filtered = (data.items || [])
          .filter((r) => (r.workflow || "").startsWith(workflowPrefix))
          .slice(0, limit);
        setRows(filtered);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load email activity");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [workflowPrefix, limit]);

  if (loading) return null;
  if (error) return null;   // silent — the audit page shows any errors globally
  if (rows.length === 0) return null;

  return (
    <section
      data-testid="email-audit-embed"
      className="rounded-md border border-divider bg-surface-alt p-3"
    >
      <div className="mb-2 flex items-center gap-2">
        <EnvelopeSimple size={14} weight="regular" className="text-ink-secondary" />
        <div className="text-[11px] font-bold uppercase tracking-wider text-ink-muted">
          {title}
        </div>
        <Link
          to="/settings/email-audit"
          data-testid="email-audit-embed-view-all"
          className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:text-brand-hover"
        >
          View all <ArrowUpRight size={11} weight="bold" />
        </Link>
      </div>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li
            key={r.id}
            data-testid={`email-audit-embed-row-${r.id}`}
            className="flex items-center gap-2 rounded-md bg-surface px-2 py-1.5 text-[12px]"
          >
            {r.delivered ? (
              <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" />
            ) : (
              <Prohibit size={12} weight="fill" className="text-amber-600 shrink-0" />
            )}
            <span
              className={`font-semibold ${
                r.delivered ? "text-emerald-800" : "text-amber-800"
              }`}
            >
              {r.delivered ? "Delivered" : "Suppressed"}
            </span>
            <span className="text-ink-muted">·</span>
            <span className="text-ink-secondary truncate flex-1" title={r.subject}>{r.subject}</span>
            <span className="text-ink-muted whitespace-nowrap">{fmtDT(r.created_at)}</span>
          </li>
        ))}
      </ul>
      {rows[0] && !rows[0].delivered && (
        <p
          data-testid="email-audit-embed-suppression-note"
          className="mt-2 text-[11px] text-amber-800"
        >
          This environment does not transmit email. The rendered message is stored
          for review — open the Email Audit page to see the full body.
        </p>
      )}
    </section>
  );
}
